import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ChevronDown, Search, X } from "react-feather";
import classNames from "classnames";
import { FormError } from "@/Components/FormError";
import { debounce } from "lodash";

export function Input({ className = "", error = false, ...props }) {
  return (
    <input
      {...props}
      className={classNames(
        "block min-h-[46px] w-full border py-2 text-gray-700 focus:border-cyan-600 focus:ring focus:ring-cyan-600 focus:ring-opacity-50",
        className,
        error ? "border-red-600" : "border-borderColor",
      )}
    />
  );
}

export function ToggleSwitch({ className = "", ...props }) {
  return (
    <label className="switch">
      <input type="checkbox" {...props} />
      <span className={`slider ${className}`} data-blockmodal={true}></span>
    </label>
  );
}

export function LabeledInput({
  title,
  help,
  unit,
  unitClass = "",
  inputClasses,
  containerClass,
  focus = false,
  ...props
}) {
  let [focused, setFocused] = useState(false);

  return (
    <div className={containerClass}>
      <label className="relative block">
        {title ? <span className="mb-1 block text-[14px]">{title}</span> : null}
        <Input
          className={`relative ${
            focus && !focused && "bg-cyan-50 ring-1"
          } ${inputClasses}`}
          type="text"
          onFocus={() => setFocused(true)}
          {...props}
        />
        {unit && (
          <span
            className={`absolute bottom-3 right-2 text-typo-inactive ${unitClass}`}
          >
            {unit}
          </span>
        )}
      </label>
      {help ? <p className="mt-1 text-sm text-gray-600">{help}</p> : null}

      {props?.error ? <FormError error={props?.error} /> : null}
    </div>
  );
}

export function Textarea({ className = "", error = false, ...props }) {
  return (
    <textarea
      {...props}
      className={classNames(
        "block w-full rounded-sm border focus:border-cyan-600 focus:ring focus:ring-cyan-600 focus:ring-opacity-50",
        className,
        error ? "border-red-600" : "border-borderColor",
      )}
    />
  );
}

// TODO Maybe autosize?
export function LabeledTextarea({ title, help, containerClass, ...props }) {
  return (
    <div className={containerClass}>
      <label>
        <span className="mb-1 block text-[14px]">{title}</span>
        <Textarea rows="6" {...props} />
      </label>
      {help ? <p className="mt-1 text-sm text-gray-600">{help}</p> : null}

      {props?.error ? <FormError error={props?.error} /> : null}
    </div>
  );
}

export const Autocomplete = forwardRef(
  (
    {
      options,
      onSelect,
      placeholder,
      name,
      defaultValue,
      error = false,
      ...props
    },
    ref,
  ) => {
    let [showDropdown, setShowDropdown] = useState(false);
    let [filter, setFilter] = useState("");
    let input = useRef();
    let container = useRef();
    let dropdown = useRef();

    useImperativeHandle(ref, () => ({
      reset() {
        input.current.value = "";
      },
    }));

    useEffect(() => {
      if (defaultValue) {
        const found = options.filter((e) => e.id === defaultValue)[0];
        if (found?.name && input?.current) {
          input.current.value = found.name;
        }
      }
    }, []);

    useEffect(() => {
      window.addEventListener("click", (e) => {
        if (
          input.current?.contains(e.target) ||
          dropdown.current?.contains(e.target)
        ) {
          return;
        }
        // Any click outside of this autocomplete
        input.current?.blur();
        setShowDropdown(false);
      });
    });

    const delayedFilterUpdate = useCallback(
      debounce((value) => {
        setFilter(value.toLowerCase());
      }, 300),
      [],
    );

    function onChangeHandler(e) {
      const { value } = e.target;
      delayedFilterUpdate.cancel();
      delayedFilterUpdate(value);
    }

    function pickOption(value, name) {
      input.current.value = name;
      onSelect({
        target: {
          value: value,
          name: input.current.name,
        },
      });
      setShowDropdown(false);
      input.current.blur();
    }

    function filteredOptions() {
      const filteredOptions = options.filter((e, i) => {
        const words = filter.split(" ");
        return words.every((word) => e.name.toLowerCase().indexOf(word) !== -1);
      });

      if (filteredOptions.length === 0) {
        return (
          <div className="absolute z-10 w-full overflow-y-scroll border bg-white">
            <div className="px-4 py-2">No search result</div>
          </div>
        );
      }

      return (
        <div className="absolute z-10 h-max max-h-96 w-full overflow-y-scroll border bg-white">
          {filteredOptions.map((e, i) => (
            <div
              className="flex cursor-pointer flex-row justify-between px-4 py-2 hover:bg-gray-100"
              key={i}
              onClick={() => {
                pickOption(e.value ?? e.id, e.label ?? e.name);
              }}
            >
              <div>{e.label ?? e.name}</div>
              {e.edit && <div className="text-blue-600">Edit</div>}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div ref={container} className="relative">
        <input
          className={`autocomplete block min-h-[46px] w-full border px-4 py-2 shadow-sm focus:border-cyan-600 focus:ring focus:ring-cyan-600 focus:ring-opacity-50 ${
            error ? "border-red-600" : "border-borderColor"
          }`}
          onChange={onChangeHandler}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          name={name}
          ref={input}
          autoComplete="off"
          {...props}
        />
        <div
          className={`${
            showDropdown ? "" : "hidden"
          } relative overflow-visible`}
          ref={dropdown}
        >
          {filteredOptions()}
        </div>
      </div>
    );
  },
);

/**
 * Autocomplete with search, use onSelect to get value
 *
 * @param {options} Expects array of objects, objects should have keys {label} or {name} for option title, and a {value} or {id} for the value.
 * @returns Rendered autocomplete
 */
export const LabeledAutocomplete = forwardRef(
  ({ title, titleRight, titleClass, help, className, ...props }, ref) => {
    const autoCompleteRef = useRef();

    useImperativeHandle(ref, () => ({
      reset() {
        autoCompleteRef.current?.reset();
      },
    }));

    return (
      <div className={`${className}`}>
        <label>
          <div className="flex flex-row justify-between">
            <span className={`mb-1 block text-[14px] ${titleClass ?? ""}`}>
              {title}
            </span>
            {titleRight ? (
              <span className="mb-1 block text-[14px]">{titleRight}</span>
            ) : null}
          </div>
        </label>
        <Autocomplete ref={autoCompleteRef} {...props} />
        {help ? <p className="mt-1 text-sm text-gray-600">{help}</p> : null}
        {props?.error ? <FormError error={props?.error} /> : null}
      </div>
    );
  },
);

export function LabeledSelectAutocomplete({
  title,
  titleRight,
  titleClass,
  help,
  className,
  placeholder = "Select an option",
  onSelect,
  options,
  defaultValue = false,
  focus = false,
  ...props
}) {
  let button = useRef();
  let container = useRef();
  let [isOpen, setIsOpen] = useState(false);
  let [value, setValue] = useState(defaultValue);
  let [focused, setFocused] = useState(false);

  useEffect(() => {
    window.addEventListener("click", (e) => {
      if (
        container.current?.contains(e.target) ||
        isOpen.current?.contains(e.target)
      ) {
        return;
      }
      // Any click outside of this autocomplete
      button.current?.blur();
      setIsOpen(false);
    });
  });

  const handleChange = (value) => {
    setValue(value);
    onSelect(value);
    setIsOpen(false);
  };

  const selectedObject = options.find((o) => o.value === value);
  const selectedValue = selectedObject ? selectedObject.name : placeholder;

  return (
    <div className={`${className}`}>
      <label>
        <div className="flex flex-row justify-between">
          <span className={`mb-1 block text-[14px] ${titleClass ?? ""}`}>
            {title}
          </span>
          {titleRight ? (
            <span className="mb-1 block text-[14px]">{titleRight}</span>
          ) : null}
        </div>
      </label>

      <div
        ref={container}
        className={`relative ${focus && !focused && "bg-cyan-50 ring-1"}`}
      >
        <button
          className={`block min-h-[46px] w-full truncate border border-borderColor bg-white px-3 py-2 pr-8 text-left text-typo shadow-sm focus:border-cyan-600 focus:ring focus:ring-cyan-600 focus:ring-opacity-50 ${
            focus && !focused && "bg-cyan-50 ring-1"
          }`}
          ref={button}
          onClick={() => {
            setIsOpen(!isOpen);
            setFocused(true);
          }}
          type="button"
          title={value === false ? placeholder : selectedValue}
        >
          {value === false ? placeholder : selectedValue}
          <ChevronDown size={16} className="absolute right-2 top-4" />
        </button>
        {options ? (
          <SearchDropdownInner
            isOpen={isOpen}
            options={options}
            handleChange={handleChange}
          />
        ) : null}
      </div>

      {help ? <p className="mt-1 text-sm text-gray-600">{help}</p> : null}

      {props?.error ? <FormError error={props?.error} /> : null}
    </div>
  );
}

export function Select({
  className = "",
  defaultOption,
  defaultValue = false,
  options,
  icon,
  noDefault,
  error = false,
  ...props
}) {
  const ref = useRef();
  const [title, setTitle] = useState();

  useEffect(() => {
    if (ref.current && ref.current.value) {
      setTitle(options.find((o) => o.value == ref.current.value)?.label ?? "");
    }
  }, []);

  useEffect(() => {
    if (defaultValue !== false && ref.current) {
      const found = options.find((o) => o.value == defaultValue);
      if (found) {
        ref.current.value = found.value;
      } else {
        ref.current.value = "";
      }
    }
  });

  return (
    <select
      {...props}
      ref={ref}
      className={classNames(
        "block min-h-[46px] w-full truncate rounded-sm border px-3 py-2 text-gray-700 shadow-sm focus:border-cyan-600 focus:ring focus:ring-cyan-600 focus:ring-opacity-50",
        className,
        error ? "border-red-600" : "border-borderColor",
        icon,
      )}
      title={title}
    >
      {noDefault !== true && (
        <option value="">
          {defaultOption ? defaultOption : `Select an option`}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.late && "⏰ "}
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function LabeledSelect({
  title,
  titleRight,
  titleClass,
  help,
  containerClass,
  ...props
}) {
  return (
    <div className={containerClass}>
      <label>
        <div className="flex flex-row justify-between">
          {title ? (
            <span className={`mb-1 block text-[14px] ${titleClass ?? ""}`}>
              {title}
            </span>
          ) : null}
          {titleRight ? (
            <span className="mb-1 block text-[14px]">{titleRight}</span>
          ) : null}
        </div>
        <Select {...props} />
      </label>
      {help ? <p className="mt-1 text-sm text-gray-600">{help}</p> : null}

      {props?.error ? <FormError error={props?.error} /> : null}
    </div>
  );
}

// TODO Arrow keys, enter on selected option, escape to close, accessibility.
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  yellowValues = false,
}) {
  let [focus, setFocus] = useState(false);
  let [text, setText] = useState("");
  let [width, setWidth] = useState(2);
  let [isOpen, setIsOpen] = useState(false);
  let input = useRef();
  let container = useRef();
  let keepFocus = useRef(false);

  function handleChange(e) {
    let el = document.createElement("span");
    // Replace spaces since they will be trimmed, and we want to
    // measure the entire text string width.
    el.textContent = e.target.value.replace(" ", "&nbsp;");
    el.style.font = getComputedStyle(input.current).font;
    document.body.appendChild(el);
    let width = el.offsetWidth;
    document.body.removeChild(el);
    setWidth(width + 2);
    setText(e.target.value);
    // The options should always show when typing.
    setIsOpen(true);
  }

  function handleKeyDown(e) {
    if (e.keyCode === 13) {
      // We'll add a new name on enter.
      if (text !== "") {
        e.preventDefault();
        onChange(value.concat({ label: text, value: "" }));
        setText("");
        setWidth(2);
      }
    } else if (e.keyCode === 8) {
      // We'll remove the last item on backspace if the text is empty.
      if (text === "") {
        onChange(value.slice(0, -1));
      }
    }
  }

  // Only show not already selected options, filtered by the search text.
  let availableOptions = options.filter(
    (option) => value.findIndex((v) => v.label === option.label) === -1,
  );
  let visibleOptions = availableOptions.filter(
    (option) => option.label.toLowerCase().search(text.toLowerCase()) !== -1,
  );

  // These props must be on every clickable element inside the input
  // or options area in order to not prevent default action and toggle focus.
  let keepFocusProps = {
    onMouseDown: (e) => {
      // Don't trigger parent mousedown when clicking here.
      e.stopPropagation();
      // We still want the input to keep focus.
      keepFocus.current = true;
    },
    onMouseUp: () => {
      // Re-focus the input on mouseup.
      keepFocus.current = false;
      input.current?.focus();
    },
  };

  return (
    <div ref={container}>
      <div
        className="autocomplete relative flex min-h-[46px] w-full border border-borderColor bg-white px-3 py-2 shadow-sm focus:border-cyan-600 focus:ring focus:ring-cyan-600 focus:ring-opacity-50"
        onMouseDown={(e) => {
          e.preventDefault();
          // If we're already focused, toggle the options visibility.
          if (focus) {
            setIsOpen((x) => !x);
          }

          input.current?.focus();
        }}
      >
        {placeholder && !text && !value.length && (
          <p
            className="absolute mt-1 select-none truncate text-typo-inactive"
            style={{ maxWidth: "calc(100% - 70px)" }}
          >
            {placeholder}
          </p>
        )}
        <div className="-mt-2 flex flex-1 flex-wrap">
          {/* The input is first in the DOM but with a flex-order that makes it visually last.
          This means it's the first element selected when tabbing, and makes wrapping the
          component in a label work as expected. */}
          <input
            style={{ width }}
            ref={input}
            type="text"
            className="_flex-1 order-1 mx-0 mb-0 mt-2 w-auto border-0 p-0 focus:ring-0"
            onFocus={() => {
              setFocus(true);
              setIsOpen(true);
            }}
            onBlur={() => {
              if (keepFocus.current) return;
              setFocus(false);
              setIsOpen(false);
              setText("");
            }}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onMouseDown={(e) => {
              // Don't toggle focus or preventing default when clicking inside
              // the input.
              e.stopPropagation();
            }}
          />
          {value.map((item, i) => (
            <div
              key={i}
              className={classNames(
                "mr-2 mt-2 flex text-sm leading-none",
                !yellowValues ? "bg-gray-200" : "bg-yellow",
              )}
              // className="mt-2 mr-2 flex bg-gray-200 text-sm leading-none"
            >
              <span className="my-1 ml-1.5 mr-1 flex items-center">
                {item.label}
              </span>
              <button
                {...keepFocusProps}
                onClick={() => onChange(value.filter((_, j) => i !== j))}
                className={classNames(
                  "rounded-br-sm rounded-tr-sm px-1",
                  !yellowValues
                    ? " hover:bg-gray-300 active:bg-gray-400"
                    : " hover:bg-yellow-dark active:bg-yellow-dark",
                )}
                // "rounded-tr-sm rounded-br-sm px-1 hover:bg-gray-300 active:bg-gray-400"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        <button
          {...keepFocusProps}
          onClick={() => {
            onChange([]);
            setText("");
          }}
          className="mr-1 text-gray-400 hover:text-typo-inactive"
        >
          <X size={16} />
        </button>
        <button className="mr-1 text-gray-400 hover:text-typo-inactive">
          <ChevronDown size={16} />
        </button>
      </div>
      <div className="relative">
        {isOpen && (
          <div className="shadow-small absolute left-0 right-0 top-0 z-10 -mt-1 border bg-white">
            {visibleOptions.length > 0 ? (
              visibleOptions.map((option, i) => (
                <button
                  {...keepFocusProps}
                  key={i}
                  onClick={() => {
                    onChange(
                      value.concat({
                        label: option.label,
                        value: option.value,
                      }),
                    );
                    // The text should reset after an option is selected.
                    setText("");
                  }}
                  className="block w-full cursor-default p-2 text-left hover:bg-gray-100 active:bg-gray-200"
                >
                  {option.label}
                </button>
              ))
            ) : (
              <p className="p-2">
                {availableOptions.length === 0
                  ? "No more options."
                  : "No options found."}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function LabeledMultiSelect({
  title,
  help,
  options,
  value,
  onChange,
  titleClass,
  placeholder = "",
  containerClass,
  yellowValues = false,
}) {
  return (
    <div className={containerClass}>
      <label>
        <span className={`mb-1 block text-[14px] ${titleClass}`}>{title}</span>
        <MultiSelect
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          yellowValues={yellowValues}
        />
      </label>
      {help ? <p className="mt-1 text-sm text-gray-600">{help}</p> : null}
    </div>
  );
}

export function SearchDropdownInner({
  isOpen = false,
  options = [],
  handleChange,
  className = "",
}) {
  let [filter, setFilter] = useState("");

  if (options.length === 0) {
    return null;
  }

  useEffect(() => {
    setFilter("");
  }, [isOpen]);

  const results = options.filter(
    (e, i) => (e.label ?? e.name ?? "N/A").toLowerCase().indexOf(filter) !== -1,
  );

  return (
    <div className={`${isOpen ? "" : "hidden"} relative z-50 overflow-visible`}>
      <div
        className={`absolute w-full border border-borderColor bg-white p-3 text-typo shadow-md ${className}`}
      >
        <div className="relative mb-4 w-full">
          <Input
            placeholder="Search"
            className="w-full bg-gray-100 pl-10"
            name="search"
            role="presentation"
            value={filter}
            onChange={(e) => setFilter(e.target.value.toLowerCase().trim())}
          />
          <Search size={18} className="absolute left-3 -mt-8 text-gray-700" />
        </div>

        <div className="flex h-auto max-h-64 flex-col overflow-y-scroll text-typo">
          {results.map((e, i) => (
            <button
              className="cursor-pointer px-3 py-1 text-left text-blue-dark underline hover:bg-primary-light"
              key={i}
              onClick={() => handleChange(e.value ?? e.id, e.label ?? e.name)}
              type="button"
            >
              {e.label ?? e.name}
            </button>
          ))}

          {results.length === 0 && (
            <p className="text-center text-gray-600">No results found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export const LabeledMultiSelectAutocomplete = forwardRef(
  (
    {
      title,
      titleRight,
      titleClass,
      help,
      icon,
      className,
      initialChecked = false,
      placeholder = "Select an option",
      onSelect,
      options,
      defaultValue = false,
      focus = false,
      ...props
    },
    ref,
  ) => {
    let button = useRef();
    let container = useRef();
    let [isOpen, setIsOpen] = useState(false);
    let [value, setValue] = useState(defaultValue);
    let [focused, setFocused] = useState(false);
    let [stateOptions, setStateOptions] = useState(
      options.map((o) => {
        /**
         * Unreadable, but basically:
         *  If we don't have a defaultValue or it is an empty array, we use initialChecked
         *  If defaultValue is set and is an array, we need to check if the option item value is in the default value array
         *  If defaultValue is set but not an array, we need to check if the option item value is equal to the default value
         */
        const isChecked = !defaultValue
          ? initialChecked
          : Array.isArray(value)
            ? value?.length === 0
              ? initialChecked
              : value.find((v) => v === o?.value || v === o?.id)
                ? true
                : false
            : value === o?.value || value === o?.id;

        return {
          value: o.value || o.id,
          label: o.label || o.name,
          title: o.label || o.name,
          checked: isChecked,
        };
      }),
    );
    const initialLoad = useRef(true);

    useEffect(() => {
      // don't rebind stuff on re-render
      if (initialLoad.current === true) {
        initialLoad.current = false;
        window.addEventListener("click", (e) => {
          if (
            container.current?.contains(e.target) ||
            isOpen.current?.contains(e.target)
          ) {
            return;
          }
          // Any click outside of this autocomplete
          button.current?.blur();
          setIsOpen(false);
        });
      } else {
        onSelect(stateOptions.filter((o) => o.checked));
      }
    }, [stateOptions]);

    useImperativeHandle(
      ref,
      () => ({
        setStateOptions,
        stateOptions,
      }),
      [stateOptions],
    );

    const handleChange = (v) => {
      const selectedObject = stateOptions.findIndex((o) => o.value === v.value);
      setStateOptions((stateOptions) => {
        const newStateOptions = [...stateOptions];
        newStateOptions[selectedObject].checked =
          !newStateOptions[selectedObject].checked;
        return newStateOptions;
      });
    };

    const handleChangeAll = (checked) => {
      const newOptions = stateOptions.map((o) => {
        return {
          ...o,
          checked: checked,
        };
      });
      setStateOptions(newOptions);
    };

    const numSelected = stateOptions.filter((o) => o.checked).length;

    return (
      <div className={`${className}`}>
        <label>
          <div className="flex flex-row justify-between">
            <span className={`mb-1 block text-[14px] ${titleClass ?? ""}`}>
              {title}
            </span>
            {titleRight ? (
              <span className="mb-1 block text-[14px]">{titleRight}</span>
            ) : null}
          </div>
        </label>

        <div
          ref={container}
          className={`relative ${focus && !focused && "bg-cyan-50 ring-1"}`}
        >
          <button
            className={`block min-h-[46px] w-full truncate border border-borderColor bg-white px-3 py-2 pr-8 text-left text-gray-700 shadow-sm focus:border-cyan-600 focus:ring focus:ring-cyan-600 focus:ring-opacity-50 ${
              focus && !focused && "bg-cyan-50 ring-1"
            }`}
            ref={button}
            onClick={() => {
              setIsOpen(!isOpen);
              setFocused(true);
            }}
            type="button"
            title={`${placeholder} (${numSelected})`}
          >
            {icon ? (
              <span className="-mb-1 mr-2 inline-block">{icon}</span>
            ) : null}
            {`${placeholder} (${numSelected})`}
            <ChevronDown size={16} className="absolute right-2 top-4" />
          </button>
          {options ? (
            <SearchDropdownSelectInner
              isOpen={isOpen}
              options={stateOptions}
              handleChange={handleChange}
              handleChangeAll={handleChangeAll}
              title={title}
            />
          ) : null}
        </div>

        {help ? <p className="mt-1 text-sm text-gray-600">{help}</p> : null}

        {props?.error ? <FormError error={props?.error} /> : null}
      </div>
    );
  },
);

export function SearchDropdownSelectInner({
  isOpen = false,
  options = [],
  handleChange,
  handleChangeAll,
  className = "",
  title = "Options",
}) {
  let [filter, setFilter] = useState("");
  let [stateOptions, setStateOptions] = useState(options);
  let [allToggled, setAllToggled] = useState(
    options.filter((o) => !o.checked).length > 0 ? false : true,
  );

  if (options.length === 0) {
    return null;
  }

  const results = stateOptions.filter(
    (e, i) => (e.label ?? "N/A").toLowerCase().indexOf(filter) !== -1,
  );
  const toggleAll = (e) => {
    const checked = !allToggled;
    handleChangeAll(checked);
    setAllToggled(checked);
  };

  useEffect(() => {
    const hasUnchecked = options.filter((o) => !o.checked).length > 0;
    setStateOptions(options);
    setAllToggled(hasUnchecked ? false : true);
  }, [options]);

  useEffect(() => {
    setFilter("");
  }, [isOpen]);

  return (
    <div className={`${isOpen ? "" : "hidden"} relative z-50 overflow-visible`}>
      <div
        className={`absolute w-fit min-w-full border border-borderColor bg-white p-3 shadow-md ${className}`}
      >
        <div className="relative mb-4 w-full">
          <Input
            placeholder="Search"
            className="w-full bg-gray-100 pl-10"
            name="search"
            role="presentation"
            value={filter}
            onChange={(e) => setFilter(e.target.value.toLowerCase())}
          />
          <Search size={18} className="absolute left-3 -mt-8 text-gray-700" />
        </div>

        <div className="flex h-auto max-h-64 flex-col overflow-y-scroll">
          {title && (
            <div
              className="mb-2 flex items-center border-b-2 border-gray-200 px-2 py-2"
              key="selectall"
            >
              <input
                type="checkbox"
                className="mr-2"
                onChange={(e) => toggleAll()}
                checked={allToggled}
              />
              <button
                className="cursor-pointer truncate px-3 py-1 text-left text-blue-dark underline hover:bg-primary-light"
                onClick={(e) => toggleAll()}
                type="button"
                title={title}
              >
                All {title}
              </button>
            </div>
          )}

          {results.map((e, i) => {
            return (
              <div className="flex items-center px-2" key={i}>
                <input
                  type="checkbox"
                  className="mr-2"
                  onChange={() => handleChange(e)}
                  checked={e.checked}
                />
                <button
                  className="cursor-pointer truncate px-3 py-1 text-left text-blue-dark underline hover:bg-primary-light"
                  onClick={() => handleChange(e)}
                  type="button"
                  title={e.title}
                >
                  {e.label}
                </button>
              </div>
            );
          })}

          {results.length === 0 && (
            <p className="text-center text-gray-600">No results found.</p>
          )}
        </div>
      </div>
    </div>
  );
}
